import React from 'react';
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { BhiDialog, Button } from 'src/UIKit';
import { VerticalScrollArea } from '../VerticalScrollArea';
import ChatFileIcon from 'src/components/ChatFileIcon/view';
import { AssetCard } from 'src/modules/assetLibrary/components/AssetCard/AssetCard';
import { useMediaLibraryDialogController } from '../../controllers';
import { MediaLibraryDialogProps } from '../../types';

const MediaLibraryDialog = (props: MediaLibraryDialogProps) => {
  const { open, onClose } = props;
  const {
    isLoadingInitial,
    isLoadingMore,
    mediaAssets,
    failedImages,
    previewAsset,
    setPreviewAsset,
    handleScroll,
    handleImageError,
    closePreview,
  } = useMediaLibraryDialogController(props);

  return (
    <BhiDialog title="Media Library" isOpen={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent>
        <VerticalScrollArea height={440} onScroll={handleScroll}>
          {isLoadingInitial ? (
            <Stack alignItems="center" py={8}>
              <CircularProgress />
            </Stack>
          ) : mediaAssets.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No media found.
            </Typography>
          ) : (
            <Stack spacing={2}>
              <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }}
                gap={2}
              >
                {mediaAssets.map(asset => (
                  <AssetCard
                    key={String(asset.id)}
                    asset={asset}
                    failedImages={failedImages}
                    onImageError={handleImageError}
                    onPreview={setPreviewAsset}
                    hideName={true}
                    enableNameEdit={false}
                    showDeleteAction={false}
                    showDownloadAction
                    previewOnClick
                    previewOnlyOnSupportedTypes={false}
                  />
                ))}
              </Box>
              {isLoadingMore && (
                <Stack alignItems="center" py={2}>
                  <CircularProgress size={20} />
                </Stack>
              )}
            </Stack>
          )}
        </VerticalScrollArea>
      </DialogContent>

      <BhiDialog title="Preview" isOpen={!!previewAsset} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogContent>
          {previewAsset && (
            <Stack alignItems="center">
              {previewAsset.mimeType?.startsWith('image/') ? (
                <Box
                  component="img"
                  src={previewAsset.url}
                  alt={previewAsset.name || 'Media'}
                  maxWidth="100%"
                  maxHeight="70vh"
                />
              ) : previewAsset.mimeType?.startsWith('video/') ? (
                <Box component="video" controls maxWidth="100%" maxHeight="70vh">
                  <source src={previewAsset.url} type={previewAsset.mimeType} />
                </Box>
              ) : previewAsset.mimeType?.startsWith('audio/') ? (
                <Box component="audio" controls width="100%">
                  <source src={previewAsset.url} type={previewAsset.mimeType} />
                </Box>
              ) : (
                <Box py={3}>
                  <ChatFileIcon url={previewAsset.url} mimeType={previewAsset.mimeType} />
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={closePreview}>
            Close
          </Button>
        </DialogActions>
      </BhiDialog>
    </BhiDialog>
  );
};

export default MediaLibraryDialog;
